import {
  DASHBOARD_STATUSES,
  HISTORICAL_DATA_REGION_IDS,
  type DashboardCategory,
  type DashboardKind,
  type DashboardStatus,
  type RequiredProduct,
} from "@/api/dataProducts.service";
import { ROUTES } from "@/routes";
import { historicalDataRegionLabel } from "@/pages/UserManagement/utils/historicalDataRegionLabel";

export type DashboardKindConfig = {
  kind: DashboardKind;
  title: string;
  singular: string;
  listRoute: string;
  newRoute: string;
  detailRoute: (id: string) => string;
};

export const DASHBOARD_KIND_CONFIG: Record<DashboardKind, DashboardKindConfig> = {
  omni: {
    kind: "omni",
    title: "Omni dashboards",
    singular: "Omni dashboard",
    listRoute: ROUTES.dataProducts.omni,
    newRoute: ROUTES.dataProducts.omniNew,
    detailRoute: ROUTES.dataProducts.omniDetail,
  },
  tableau: {
    kind: "tableau",
    title: "Historical Data dashboards",
    singular: "Historical Data dashboard",
    listRoute: ROUTES.dataProducts.historicalData,
    newRoute: ROUTES.dataProducts.historicalDataNew,
    detailRoute: ROUTES.dataProducts.historicalDataDetail,
  },
};

export const CATEGORY_LABELS: Record<DashboardCategory, string> = {
  submissions_analysis: "Submissions analysis",
  client_experience: "Client experience",
  historical_data: "Historical data",
};

export const REQUIRED_PRODUCT_LABELS: Record<RequiredProduct, string> = {
  sars: "SARS",
  cxi_report: "CXI report",
  historical_data: "Historical Data",
};

export const STATUS_LABELS: Record<DashboardStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

export const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABELS) as DashboardCategory[]).map(
  value => ({ value, label: CATEGORY_LABELS[value] }),
);

export const REQUIRED_PRODUCT_OPTIONS = (
  Object.keys(REQUIRED_PRODUCT_LABELS) as RequiredProduct[]
).map(value => ({ value, label: REQUIRED_PRODUCT_LABELS[value] }));

export const STATUS_OPTIONS = DASHBOARD_STATUSES.map(value => ({
  value,
  label: STATUS_LABELS[value],
}));

export const REGION_OPTIONS = HISTORICAL_DATA_REGION_IDS.map(value => ({
  value,
  label: historicalDataRegionLabel(value),
}));

export const EDITION_SUGGESTIONS = ["2026", "all_years"] as const;

export const DEFAULT_TABLEAU_HOST = "https://public.tableau.com";

const ALL_YEARS_EDITION = "all_years";

export function formatEdition(edition: string | null): string {
  if (!edition) return "—";
  if (edition === ALL_YEARS_EDITION) return "All years";
  if (/^\d{4}$/.test(edition)) return `${edition} edition`;
  const words = edition.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function compareEditions(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (a === ALL_YEARS_EDITION) return -1;
  if (b === ALL_YEARS_EDITION) return 1;
  return b.localeCompare(a, undefined, { numeric: true });
}

export function isDashboardStatus(value: string): value is DashboardStatus {
  return (DASHBOARD_STATUSES as readonly string[]).includes(value);
}
